export const DEFAULT_COL_WIDTH = 120;

// ============================================================================
// CSS-КЛАССЫ ПОДСВЕТКИ ЯЧЕЕК И ЗАГОЛОВКОВ
// ============================================================================

/**
 * Константы CSS-классов для условного форматирования ячеек и заголовков колонок.
 * Стили определены в src/styles/components/handsontable-theme.scss.
 *
 * Правило использования в cells():
 *   meta.className = CELL_CLS.deviationNegative;
 * TrackedTable автоматически смержит базовый класс колонки (htCenter/htLeft/htRight)
 * и классы трекинга, дедуплицируя токены.
 */
export const CELL_CLS = {
  /** Красный фон (#fef2f2) + bold — отрицательное отклонение */
  deviationNegative:   'deviation-negative',
  /** Зелёный фон (#ecfdf5) + bold — положительное отклонение */
  deviationPositive:   'deviation-positive',
  /** Оранжевый фон (#fff7ed) — выделение колонки базового плана закупок */
  purchasePlanOrange:  'ht-purchase-plan-orange',
  /** Затемнение недоступной/заблокированной ячейки */
  dimmed:              'htDimmed',
  /** Скрытая колонка (ширина 1px, не отображается) */
  hidden:              'htHidden',
  /** Фиолетовый заголовок колонки (#9333ea) — добавляется через classList.add в afterGetColHeader */
  highlightedHeader:   'ht-highlighted-header',
  /** Синий заголовок колонки (#3b82f6) — добавляется через classList.add в afterGetColHeader */
  highlightedHeaderBlue: 'ht-highlighted-header-blue',
  /** Оранжевый заголовок колонки (#ea580c) — добавляется через classList.add в afterGetColHeader */
  highlightedHeaderOrange: 'ht-highlighted-header-orange',
  /** Зелёный заголовок колонки (#059669) — добавляется через classList.add в afterGetColHeader */
  highlightedHeaderGreen: 'ht-highlighted-header-green',
  /** Ячейка превышает порог — красный (#fef2f2) + bold, Stage 5 */
  cellExceeded:        'stage5-cell-exceeded',
  /** Ячейка в пределах нормы — зелёный (#ecfdf5) + bold, Stage 5 */
  cellWithin:          'stage5-cell-within',
  /** Серый фон + серый текст — неактивная строка (is_active=false) в таблицах плана продаж */
  rowInactive:         'row-inactive',
  /** Строка ИТОГО (последняя строка таблицы) — жирный текст, приглушённый фон */
  totalRow:            'ht-total-row',
  /** Кликабельное число с детализацией (поповер по клику): курсор-указатель + пунктирное подчёркивание */
  drilldown:           'ht-cell-drilldown',
} as const;

export type CellCls = (typeof CELL_CLS)[keyof typeof CELL_CLS];

/**
 * Составляет итоговый className для ячейки в cells() callback.
 *
 * TrackedTable мержит базовый класс колонки сам — поэтому передавать colDef
 * необязательно. Утилита полезна для таблиц вне TrackedTable или когда нужно
 * явно скомбинировать несколько highlight-классов.
 *
 * @example
 * // Внутри cells(): просто верните нужный класс
 * meta.className = CELL_CLS.deviationNegative;
 *
 * // Несколько классов — используйте cellClass
 * meta.className = cellClass(CELL_CLS.deviationNegative, CELL_CLS.dimmed);
 */
export function cellClass(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}
export const RESPONSIVE_SIDE_GUTTER = 0;

// ============================================================================
// КУЛЬТУРА ЧИСЕЛ
// ============================================================================

/**
 * Культура для numbro.js.
 *
 * Требование проекта: формат **1 000,10** (пробелы в тысячах + запятая как десятичный разделитель).
 * Handsontable использует numbro.js для numericFormat.
 * Для отображения в русской локали (ru-RU) Handsontable настраивается через language prop.
 */
export const NUMBRO_CULTURE = 'ru-RU';

// ============================================================================
// NUMBRO.JS FORMAT OBJECTS (РЕКОМЕНДУЕТСЯ) ⭐
// ============================================================================

/**
 * Целые числа с разделителем тысяч
 * Пример: 1000 → 1 000 | 1234567 → 1 234 567
 * Использование: количество, объемы
 */
export const NUMBRO_INT = {
  thousandSeparated: true,
  mantissa: 0
} as const;

/**
 * Умный формат для денежных значений (до 2 знаков)
 * Пример: 1000 → 1 000 | 1000.5 → 1 000,5 | 1000.55 → 1 000,55
 * Использование: цены, выручка, любые денежные значения
 * ⭐ РЕКОМЕНДУЕТСЯ для большинства денежных полей
 */
export const NUMBRO_CURRENCY = {
  thousandSeparated: true,
  mantissa: 2,
  trimMantissa: true,      // убирает .00 если число целое
  optionalMantissa: true   // показывает десятичные только если нужны
} as const;

/**
 * Коэффициенты и проценты (до 5 знаков)
 * Пример: 1.2 → 1.2 | 1.23456 → 1.23456 | 1 → 1
 * Использование: КНП, коэффициенты, научные расчеты
 */
export const NUMBRO_COEFFICIENT = {
  thousandSeparated: true,
  mantissa: 5,
  trimMantissa: true,
  optionalMantissa: true
} as const;

/**
 * Целые числа БЕЗ разделителей (для годов, идентификаторов)
 * Пример: 2026 → 2026 | 1234 → 1234
 * Использование: годы, ID, коды
 */
export const NUMBRO_PLAIN_INT = {
  thousandSeparated: false,
  mantissa: 0
} as const;
